import { z } from 'zod';

/**
 * Phase 6 enums: calls and huddles (PLAN §3.1 "Calls and huddles", D-026, D-039, D-044, D-094).
 * The database CHECK constraints of `calls`, `call_participants` and `livekit_webhook_receipts` are
 * built from these lists (packages/db/src/schema/calls.ts). Recording (`call_recordings`) is not
 * built in this wave (D-647), so its enums are not here either.
 */

// ---------------------------------------------------------------- calls

/** `calls.kind`: a ring-style call, or the drop-in huddle of a conversation (D-044). */
export const CALL_KINDS = ['call', 'huddle'] as const;
export type CallKind = (typeof CALL_KINDS)[number];
export const callKindSchema = z.enum(CALL_KINDS);

/** `calls.media`: the starting mode; video can be switched on later in the call. */
export const CALL_MEDIA = ['audio', 'video'] as const;
export type CallMedia = (typeof CALL_MEDIA)[number];
export const callMediaSchema = z.enum(CALL_MEDIA);

/** `calls.status`. Huddles start `active`; the others start `ringing`. */
export const CALL_STATUSES = [
  'ringing',
  'active',
  'ended',
  'missed',
  'declined',
  'cancelled',
] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];
export const callStatusSchema = z.enum(CALL_STATUSES);

/** The statuses of a call that is still live (it holds the conversation's one live call). */
export const LIVE_CALL_STATUSES = ['ringing', 'active'] as const satisfies readonly CallStatus[];

/** The statuses a call can never leave. */
export const FINAL_CALL_STATUSES = [
  'ended',
  'missed',
  'declined',
  'cancelled',
] as const satisfies readonly CallStatus[];

/** `calls.end_reason`. */
export const CALL_END_REASONS = [
  'hung_up',
  'last_left',
  'ring_timeout',
  'declined',
  'cancelled',
  'ended_by_admin',
  'room_finished',
] as const;
export type CallEndReason = (typeof CALL_END_REASONS)[number];
export const callEndReasonSchema = z.enum(CALL_END_REASONS);

// ---------------------------------------------------------------- participants

/** `call_participants.role`. */
export const CALL_PARTICIPANT_ROLES = ['caller', 'callee', 'participant'] as const;
export type CallParticipantRole = (typeof CALL_PARTICIPANT_ROLES)[number];
export const callParticipantRoleSchema = z.enum(CALL_PARTICIPANT_ROLES);

/** `call_participants.ring_status`; NULL for people who joined without being rung. */
export const CALL_RING_STATUSES = [
  'ringing',
  'accepted',
  'declined',
  'missed',
  'cancelled',
] as const;
export type CallRingStatus = (typeof CALL_RING_STATUSES)[number];
export const callRingStatusSchema = z.enum(CALL_RING_STATUSES);

/** `POST /calls/:callId/decline` → `reason` (`busy` = already in another call). */
export const CALL_DECLINE_REASONS = ['declined', 'busy'] as const;
export type CallDeclineReason = (typeof CALL_DECLINE_REASONS)[number];
export const callDeclineReasonSchema = z.enum(CALL_DECLINE_REASONS);

// ---------------------------------------------------------------- LiveKit webhooks

/**
 * `livekit_webhook_receipts.event_type`: the LiveKit events that change call state. Other events
 * LiveKit sends (room_started, track_published, …) are acknowledged and ignored without a receipt.
 * `egress_ended` belongs to recording, which is not built yet (D-647).
 */
export const LIVEKIT_WEBHOOK_EVENT_TYPES = [
  'participant_joined',
  'participant_left',
  'room_finished',
  'egress_ended',
] as const;
export type LiveKitWebhookEventType = (typeof LIVEKIT_WEBHOOK_EVENT_TYPES)[number];

// ---------------------------------------------------------------- limits and settings

export const CALL_LIMITS = Object.freeze({
  /** Hard ceiling (SPEC §5, `calls.peak_participants` CHECK); `calls.maxParticipants` may lower it. */
  maxParticipants: 50,
  /** `calls.ringTimeoutSeconds` default and bounds (PLAN §4.4, D-094). */
  ringTimeoutSeconds: 45,
  minRingTimeoutSeconds: 15,
  maxRingTimeoutSeconds: 120,
  /** At most this many people rung in one `POST /calls` or `/ring` (channels, `ringUserIds`). */
  maxRingInvitees: 50,
  /** A LiveKit join token is only needed to connect (10 minutes, PLAN §5). */
  joinTokenTtlSeconds: 600,
  /**
   * Default `TURN_CREDENTIAL_TTL_SECONDS`: a day (D-663). The client hands the credentials to
   * the browser once, at connect time, and coturn checks their expiry on every allocation refresh,
   * so a shorter lifetime cuts relayed media off mid-call; the join token stays at 10 minutes.
   */
  turnCredentialTtlSeconds: 86_400,
  /** A live room with nobody in it for this long is closed by the stale-room sweep. */
  staleRoomSeconds: 120,
  /**
   * How long a person whose last connection dropped (a LiveKit `participant_left`) may take to come
   * back before that counts as leaving the call (D-661): a page reload, a network switch or LiveKit
   * replacing a device's old connection must not end a 1:1 call. Hanging up in the app is immediate.
   */
  leaveGraceSeconds: 12,
  /** Ringing the same person again from the same call needs this long since their last ring (D-664). */
  ringCooldownSeconds: 30,
  /**
   * Wave 5 (D-1031, D-1232): the hard ceiling of a webinar room only (`calls.participant_cap`, and
   * the `calls.peak_participants` CHECK): every seated attendee plus every presenter — 500 + 10.
   *
   * `CALENDAR_LIMITS` owns the attendee number, because it is what validates
   * `meeting_webinars.capacity`. This is written out rather than imported from it: `enums/calls`
   * is pulled in by almost everything, and importing the calendar enums here put the whole
   * calendar module graph into every consumer (it timed out the web tests). `calls.test.ts` holds
   * the two to 510 = attendees + presenters, so they still cannot drift. Ordinary calls and
   * general meetings keep `maxParticipants`.
   *
   * An attendee is a `listener` — subscribe-only, no publish, no data, hidden (D-1029) — so a full
   * room is a broadcast fan-out, not 510 people in a meeting. The SFU cost is in the publishers,
   * which is why the presenter half stays small.
   */
  webinarMaxParticipants: 510,
  /** A meeting room holding only outside guests ends after this long (D-1028). */
  guestOnlyGraceSeconds: 300,
  /** A waiting guest polls the session every 3 s, an admitted one every 15 s (D-1028). */
  lobbyPollMs: 3000,
  admittedPollMs: 15000,
  /** A waiting entry not seen for this long (the tab was closed) is hidden and expired (D-1028). */
  lobbyStaleSeconds: 45,
});

/** Why `GET /calls/availability` says calls cannot be used. */
export const CALL_UNAVAILABLE_REASONS = ['livekit_not_configured', 'disabled_by_admin'] as const;
export type CallUnavailableReason = (typeof CALL_UNAVAILABLE_REASONS)[number];
export const callUnavailableReasonSchema = z.enum(CALL_UNAVAILABLE_REASONS);

/** `messages.subtype` of the one system message per call (D-039); see `callMessageMetadataSchema`. */
export const CALL_MESSAGE_SUBTYPE = 'call' as const;

/**
 * Wave 5 (D-1231): `call_recordings.status`. Recording exists for WEBINARS ONLY — D-900 still
 * forbids it for ordinary calls and huddles, and `call_recordings_webinar_guard` enforces that in
 * the database, not just in the service.
 *
 *   pending   the egress has been asked for and has not confirmed
 *   active    LiveKit is recording
 *   stopping  a stop was asked for, or the room ended; the file is not stored yet
 *   completed the file is in object storage and `file_id` points at it
 *   failed    the egress never started, or died; `error` says what happened
 *   deleted   the file has been removed by retention or by hand; the row stays as the record
 */
export const RECORDING_STATUSES = [
  'pending',
  'active',
  'stopping',
  'completed',
  'failed',
  'deleted',
] as const;
export type RecordingStatus = (typeof RECORDING_STATUSES)[number];
export const recordingStatusSchema = z.enum(RECORDING_STATUSES);

/** How a recording was started: a presenter pressed record, or the webinar was set to record itself. */
export const RECORDING_TRIGGERS = ['manual', 'automatic'] as const;
export type RecordingTrigger = (typeof RECORDING_TRIGGERS)[number];
export const recordingTriggerSchema = z.enum(RECORDING_TRIGGERS);

/** Why a recording ended up `deleted`. */
export const RECORDING_DELETE_REASONS = ['retention', 'by_hand', 'webinar_deleted'] as const;
export type RecordingDeleteReason = (typeof RECORDING_DELETE_REASONS)[number];
export const recordingDeleteReasonSchema = z.enum(RECORDING_DELETE_REASONS);

/** Recording limits (D-1231). */
export const RECORDING_LIMITS = Object.freeze({
  /** A recording is kept this long by default before the retention sweep removes the file. */
  defaultRetentionDays: 90,
  minRetentionDays: 1,
  maxRetentionDays: 365,
  /**
   * A `pending` egress that has not confirmed in this long is `failed`: LiveKit took the request
   * and never started, and a recording nobody is told about is worse than one that failed loudly.
   */
  startTimeoutSeconds: 60,
  /** A `stopping` egress that has not produced a file in this long is `failed`. */
  stopTimeoutSeconds: 900,
});
